/* MGNSN-PADEL motion + interactions. Depends on gsap, ScrollTrigger, Lenis (all optional). */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = window.Shopify && window.Shopify.designMode;
  const hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

  /* ---------- Smooth scroll ---------- */
  let lenis = null;
  function initLenis() {
    if (reduced || designMode || lenis || typeof window.Lenis === 'undefined') return;
    if (!document.body.hasAttribute('data-mg-smooth')) return;
    lenis = new window.Lenis({ lerp: 0.1, smoothWheel: true });
    if (hasGsap) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((t) => lenis.raf(t * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => {
        lenis.raf(t);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
    }
  }

  /* ---------- Split headline ---------- */
  function splitLines(el) {
    if (el.dataset.mgSplitDone) return;
    el.dataset.mgSplitDone = '1';
    const lines = el.innerHTML.split(/<br\s*\/?>/i);
    el.innerHTML = lines
      .map((l) => `<span class="mg-split-line"><span>${l.trim()}</span></span>`)
      .join('');
  }

  function initSplit(root) {
    root.querySelectorAll('[data-mg-split]').forEach((el) => {
      splitLines(el);
      if (reduced || !hasGsap || designMode) return;
      window.gsap.from(el.querySelectorAll('.mg-split-line > span'), {
        yPercent: 110,
        duration: 1.2,
        ease: 'expo.out',
        stagger: 0.09,
        delay: el.dataset.mgSplit === 'hero' ? 0.15 : 0,
        scrollTrigger: el.dataset.mgSplit === 'hero' ? undefined : { trigger: el, start: 'top 85%' },
      });
    });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    const items = root.querySelectorAll('[data-mg-reveal]');
    if (!('IntersectionObserver' in window) || reduced) {
      items.forEach((i) => i.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const delay = Number(e.target.dataset.mgReveal || 0);
          setTimeout(() => e.target.classList.add('is-in'), delay);
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach((i) => io.observe(i));
  }

  /* ---------- Parallax media ---------- */
  function initParallax(root) {
    if (reduced || !hasGsap || designMode) return;
    root.querySelectorAll('[data-mg-parallax]').forEach((el) => {
      window.gsap.to(el, {
        yPercent: Number(el.dataset.mgParallax || 15),
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top top', end: 'bottom top', scrub: true },
      });
    });
  }

  /* ---------- Manifesto word highlight ---------- */
  function initManifesto(root) {
    root.querySelectorAll('[data-mg-manifesto]').forEach((el) => {
      if (!el.dataset.mgWords) {
        el.dataset.mgWords = '1';
        const walk = (node) => {
          [...node.childNodes].forEach((child) => {
            if (child.nodeType === 3) {
              const frag = document.createDocumentFragment();
              child.textContent.split(/(\s+)/).forEach((part) => {
                if (!part.trim()) return frag.appendChild(document.createTextNode(part));
                const s = document.createElement('span');
                s.className = 'mg-word';
                s.textContent = part;
                frag.appendChild(s);
              });
              child.replaceWith(frag);
            } else if (child.nodeType === 1) walk(child);
          });
        };
        walk(el);
      }
      const words = el.querySelectorAll('.mg-word');
      if (reduced || !hasGsap || designMode) {
        words.forEach((w) => w.classList.add('is-on'));
        return;
      }
      window.ScrollTrigger.create({
        trigger: el,
        start: 'top 75%',
        end: 'bottom 45%',
        scrub: true,
        onUpdate: (self) => {
          const n = Math.round(self.progress * words.length);
          words.forEach((w, i) => w.classList.toggle('is-on', i < n));
        },
      });
    });
  }

  /* ---------- Drag-to-scroll rails ---------- */
  function initRails(root) {
    root.querySelectorAll('[data-mg-rail]').forEach((rail) => {
      if (rail.dataset.mgRailDone) return;
      rail.dataset.mgRailDone = '1';
      const bar = rail.parentElement.querySelector('.mg-rail__progress span');
      const update = () => {
        if (!bar) return;
        const max = rail.scrollWidth - rail.clientWidth;
        const visible = rail.clientWidth / rail.scrollWidth;
        bar.style.width = `${Math.max(visible, max > 0 ? rail.scrollLeft / max : 1) * 100}%`;
      };
      rail.addEventListener('scroll', update, { passive: true });
      update();

      let down = false, startX = 0, startLeft = 0, moved = false;
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false;
        startX = e.clientX; startLeft = rail.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5) { moved = true; rail.classList.add('is-dragging'); }
        rail.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        down = false;
        setTimeout(() => rail.classList.remove('is-dragging'), 0);
      });
      rail.addEventListener('click', (e) => { if (moved) e.preventDefault(); }, true);
    });
  }

  /* ---------- Countdown ---------- */
  function initCountdowns(root) {
    root.querySelectorAll('[data-mg-countdown]').forEach((el) => {
      const target = new Date(el.dataset.mgCountdown).getTime();
      if (Number.isNaN(target)) return (el.hidden = true);
      const set = (k, v) => {
        const node = el.querySelector(`[data-unit="${k}"]`);
        if (node) node.textContent = String(v).padStart(2, '0');
      };
      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          el.hidden = true;
          return clearInterval(timer);
        }
        set('d', Math.floor(diff / 864e5));
        set('h', Math.floor((diff / 36e5) % 24));
        set('m', Math.floor((diff / 6e4) % 60));
        set('s', Math.floor((diff / 1e3) % 60));
      };
      const timer = setInterval(tick, 1000);
      tick();
    });
  }

  /* ---------- Cart helpers (Dawn drawer integration) ---------- */
  async function addToCart(items, trigger) {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    trigger && trigger.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');
      if (drawer && data.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else {
        window.location.href = window.routes.cart_url;
      }
      return true;
    } catch (err) {
      alertInline(trigger, err.message);
      return false;
    } finally {
      trigger && trigger.removeAttribute('aria-busy');
    }
  }

  function alertInline(trigger, msg) {
    if (!trigger) return;
    const host = trigger.closest('[data-mg-msg-host]') || trigger.parentElement;
    let box = host.querySelector('.mg-error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'mg-error';
      box.setAttribute('role', 'alert');
      host.appendChild(box);
    }
    box.textContent = msg;
  }

  // Upsell buttons (cart drawer re-renders, so delegate)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-mg-add]');
    if (!btn) return;
    e.preventDefault();
    addToCart([{ id: Number(btn.dataset.mgAdd), quantity: 1 }], btn);
  });

  /* ---------- Kit builder ---------- */
  function initKits(root) {
    root.querySelectorAll('[data-mg-kit]').forEach((kit) => {
      if (kit.dataset.mgKitDone) return;
      kit.dataset.mgKitDone = '1';
      const inputs = [...kit.querySelectorAll('input[type="checkbox"]')];
      const totalEl = kit.querySelector('[data-mg-kit-total]');
      const msgEl = kit.querySelector('[data-mg-kit-msg]');
      const btn = kit.querySelector('[data-mg-kit-add]');
      const minItems = Number(kit.dataset.minItems || 3);
      const pct = Number(kit.dataset.discount || 0);
      const fmt = new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency',
        currency: kit.dataset.currency || 'EUR',
      });
      const strings = JSON.parse(kit.querySelector('[data-mg-kit-strings]').textContent);

      const update = () => {
        const sel = inputs.filter((i) => i.checked);
        const sum = sel.reduce((a, i) => a + Number(i.dataset.price), 0) / 100;
        const unlocked = pct > 0 && sel.length >= minItems;
        totalEl.innerHTML = unlocked
          ? `${fmt.format(sum * (1 - pct / 100))}<small><s>${fmt.format(sum)}</s></small>`
          : fmt.format(sum);
        const left = minItems - sel.length;
        msgEl.textContent =
          pct <= 0 ? strings.count.replace('[n]', sel.length)
          : unlocked ? strings.unlocked.replace('[pct]', pct)
          : strings.more.replace('[n]', left).replace('[pct]', pct);
        btn.disabled = sel.length === 0;
      };
      inputs.forEach((i) => i.addEventListener('change', update));
      btn.addEventListener('click', async () => {
        const items = inputs.filter((i) => i.checked).map((i) => ({ id: Number(i.value), quantity: 1 }));
        if (items.length) await addToCart(items, btn);
      });
      update();
    });
  }

  /* ---------- Sticky ATC (product page) ---------- */
  function initStickyAtc() {
    const bar = document.querySelector('[data-mg-sticky-atc]');
    if (!bar || bar.dataset.done) return;
    bar.dataset.done = '1';
    const mainBtn = document.querySelector('.product-form__submit');
    if (!mainBtn) return (bar.hidden = true);
    const io = new IntersectionObserver(([e]) => {
      bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
    });
    io.observe(mainBtn);
    bar.querySelector('button').addEventListener('click', () => {
      if (mainBtn.disabled) {
        mainBtn.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      } else {
        mainBtn.click();
      }
    });
  }

  /* ---------- Boot ---------- */
  function init(root = document) {
    initSplit(root);
    initReveal(root);
    initParallax(root);
    initManifesto(root);
    initRails(root);
    initCountdowns(root);
    initKits(root);
  }

  const boot = () => {
    document.documentElement.classList.add('mg-js');
    if (hasGsap) window.gsap.registerPlugin(window.ScrollTrigger);
    initLenis();
    init();
    initStickyAtc();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    if (hasGsap) window.ScrollTrigger.refresh();
  });
})();
